// The timing of the chapter checklist's rewards, as pure functions of a clock.
//
// Three beats, each a pose read off its own elapsed milliseconds (the same convention as the clue
// callouts and the wave reveal): nothing here owns a timer, so a frame-driven owner can pose any
// instant exactly and a test can assert the shape of a beat without a renderer.
//
//   * the STAMP -- a task's check landing in its row (chapter_checklist.ts)
//   * the TOAST -- the "task complete" plaque mid-screen, whose diamond then flies to that row
//   * the FANFARE -- the chapter-complete banner, which ends by flying into the checklist panel
//     (both in chapter_celebration.ts)

export const clamp01 = (t: number) => Math.max(0, Math.min(1, t))
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t
export const easeOutCubic = (t: number) => 1 - Math.pow(1 - clamp01(t), 3)
export const easeInCubic = (t: number) => Math.pow(clamp01(t), 3)
export const easeInOutCubic = (t: number) => {
    const x = clamp01(t)
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
}
/** Overshoots its target and settles back: the "pop" every arrival here is built on. */
export const easeOutBack = (t: number, s = 1.9) => {
    const x = clamp01(t) - 1
    return 1 + (s + 1) * x * x * x + s * x * x
}
/** Progress through the window [from, to] of a clock, clamped. */
export const span = (ms: number, from: number, to: number) => clamp01((ms - from) / Math.max(1, to - from))

export const lerpColor = (a: number, b: number, t: number) => {
    const k = clamp01(t)
    const ch = (shift: number) => Math.round(lerp((a >> shift) & 0xff, (b >> shift) & 0xff, k))
    return (ch(16) << 16) | (ch(8) << 8) | ch(0)
}

/** A point on the quadratic curve through `from` and `to` that bows through `via`. */
export const quadPoint = (
    from: { x: number; y: number },
    via: { x: number; y: number },
    to: { x: number; y: number },
    t: number,
) => {
    const u = 1 - t
    return {
        x: u * u * from.x + 2 * u * t * via.x + t * t * to.x,
        y: u * u * from.y + 2 * u * t * via.y + t * t * to.y,
    }
}

// ---- The stamp --------------------------------------------------------------------------------

export const STAMP_MS = 950

/**
 * A check landing in its row. The socket slams in oversized and settles, the check draws itself on
 * the way down, a diamond ring and a ring of sparks leave it, a band of light crosses the row, and
 * the label flashes gold before cooling to the done colour.
 */
export const stampPose = (ms: number) => ({
    // Oversized on the first frame, so the change is never a quiet swap of one sprite for another.
    socketScale: ms >= STAMP_MS ? 1 : lerp(2.1, 1, easeOutBack(span(ms, 0, 320), 2.6)),
    checkDraw: easeOutCubic(span(ms, 60, 300)),
    ringT: span(ms, 0, 520),
    sparkT: span(ms, 0, 560),
    sweepT: easeInOutCubic(span(ms, 90, 640)),
    // 1 = the label in full gold, 0 = settled to the done colour.
    labelGold: ms < 120 ? 1 : 1 - easeInCubic(span(ms, 380, STAMP_MS)),
    // The whole panel's rim answers the stamp, briefly.
    panelFlash: 1 - easeOutCubic(span(ms, 0, 700)),
    done: ms >= STAMP_MS,
})

// ---- The toast --------------------------------------------------------------------------------

export const TOAST_ENTER_MS = 300
/** How long the toast holds when nothing is waiting behind it, and when something is. */
export const TOAST_HOLD_MS = 1250
export const TOAST_HOLD_QUEUED_MS = 520
export const TOAST_LEAVE_MS = 260
export const COMET_MS = 560

export const toastTimeline = (holdMs: number) => {
    const leaveAt = TOAST_ENTER_MS + holdMs
    const cometAt = leaveAt + 40
    return { leaveAt, cometAt, arriveAt: cometAt + COMET_MS, endAt: cometAt + COMET_MS + 40 }
}

export const toastPose = (ms: number, holdMs: number) => {
    const { leaveAt, cometAt, arriveAt } = toastTimeline(holdMs)
    const enter = span(ms, 0, TOAST_ENTER_MS)
    const leave = span(ms, leaveAt, leaveAt + TOAST_LEAVE_MS)
    return {
        alpha: Math.min(easeOutCubic(enter * 1.6), 1 - easeInCubic(leave)),
        scale: ms < leaveAt ? lerp(0.45, 1, easeOutBack(enter, 2.2)) : lerp(1, 0.86, easeOutCubic(leave)),
        // Rises the last few pixels into place, and lifts away as it goes.
        lift: ms < leaveAt ? 10 * (1 - easeOutCubic(enter)) : -12 * easeOutCubic(leave),
        burstT: span(ms, 40, 640),
        shineT: easeInOutCubic(span(ms, 260, 820)),
        // The diamond flies from the toast to the row: 0 before it sets off, 1 on arrival.
        cometT: ms < cometAt ? -1 : easeInOutCubic(span(ms, cometAt, arriveAt)),
        arrived: ms >= arriveAt,
    }
}

// ---- The fanfare ------------------------------------------------------------------------------

export const FANFARE_HOLD_MS = 2600
export const FANFARE_DOCK_MS = 620
export const fanfareTimeline = () => {
    const dockAt = FANFARE_HOLD_MS
    return { dockAt, arriveAt: dockAt + FANFARE_DOCK_MS, endAt: dockAt + FANFARE_DOCK_MS + 60 }
}

/**
 * The chapter-complete banner. A flash and a shockwave, rays opening behind the title, the title
 * slamming in, a COMPLETE stamp landing across it, embers climbing past while it holds -- then the
 * whole banner draws itself into a comet and flies into the checklist panel, which is where the
 * "Next Chapter" choice is waiting.
 */
export const fanfarePose = (ms: number) => {
    const { dockAt, arriveAt } = fanfareTimeline()
    const dock = span(ms, dockAt, arriveAt)
    return {
        flash: ms < 0 ? 0 : 1 - easeOutCubic(span(ms, 0, 420)),
        ringT: span(ms, 0, 900),
        raysOpen: easeOutBack(span(ms, 60, 700), 1.4),
        raysAlpha: Math.min(easeOutCubic(span(ms, 60, 400)), 1 - easeInCubic(span(ms, dockAt - 300, dockAt + 120))),
        overlineAlpha: easeOutCubic(span(ms, 120, 420)),
        titleScale: lerp(1.6, 1, easeOutBack(span(ms, 140, 520), 1.7)),
        titleAlpha: easeOutCubic(span(ms, 140, 340)),
        rule: easeOutCubic(span(ms, 420, 900)),
        stampScale: ms < 620 ? 0 : lerp(2.4, 1, easeOutBack(span(ms, 620, 880), 2.4)),
        stampAlpha: easeOutCubic(span(ms, 620, 720)),
        stampBurstT: span(ms, 640, 1200),
        shineT: easeInOutCubic(span(ms, 1100, 1800)),
        embers: ms > 200 && ms < dockAt + 200,
        // The banner collapsing into the comet that flies to the panel.
        bannerScale: lerp(1, 0.08, easeInCubic(dock)),
        bannerAlpha: 1 - easeInCubic(span(ms, dockAt, dockAt + FANFARE_DOCK_MS * 0.75)),
        cometT: ms < dockAt ? -1 : easeInOutCubic(dock),
        arrived: ms >= arriveAt,
    }
}

// ---- The call to action -----------------------------------------------------------------------

/**
 * The button waiting in the panel LIGHTS UP rather than appearing: it arrives as an unlit plate,
 * light crosses it from the left with a soft edge leading, one shine follows, and then it is STILL.
 * It is an invitation, not an alarm -- the player may stay in a finished chapter as long as they
 * like, and a button that breathes and glints every few seconds nags them toward the next one.
 */
export const CTA_IGNITE_FROM = 140
export const CTA_IGNITE_MS = 520
const CTA_LIT_AT = CTA_IGNITE_FROM + CTA_IGNITE_MS

export const ctaPose = (ms: number) => ({
    // The unlit plate settling into place before it catches.
    plateAlpha: easeOutCubic(span(ms, 0, 180)),
    rise: 5 * (1 - easeOutCubic(span(ms, 0, 260))),
    ignite: easeInOutCubic(span(ms, CTA_IGNITE_FROM, CTA_LIT_AT)),
    // The soft edge that leads the light across.
    edge:
        ms > CTA_IGNITE_FROM && ms < CTA_LIT_AT + 120
            ? Math.sin(Math.PI * span(ms, CTA_IGNITE_FROM, CTA_LIT_AT + 120))
            : 0,
    // A brief warmth as the light lands, gone within a second.
    glow: ms < CTA_LIT_AT - 80 ? 0 : 1 - easeOutCubic(span(ms, CTA_LIT_AT - 80, CTA_LIT_AT + 900)),
    // One shine, once.
    shineT:
        ms > CTA_LIT_AT + 100 && ms < CTA_LIT_AT + 800
            ? easeInOutCubic(span(ms, CTA_LIT_AT + 100, CTA_LIT_AT + 800))
            : -1,
})

// ---- The announcement -------------------------------------------------------------------------

/**
 * The chapter's title card (chapter_announcement.ts), arriving. A hearth glow comes up from below
 * the frame, the chapter's crest pops in and IGNITES (a flash, a ring, sparks), a rule draws itself
 * out from it, and the words follow one at a time: the overline, then the title rising in gold-hot
 * and cooling to cream, then the line under it, then the actions, Begin lighting up as the
 * checklist's call to action does. After that only the embers move.
 */
export const ANNOUNCE_IGNITE_AT = 380
export const ANNOUNCE_ACTIONS_AT = 1050
/** Begin answers once its plate has arrived. A click on the card before then skips ahead to here. */
export const ANNOUNCE_READY_MS = ANNOUNCE_ACTIONS_AT + 200
export const ANNOUNCE_LAUNCH_MS = 820
export const ANNOUNCE_LATER_MS = 480

export const announcePose = (ms: number) => ({
    hearth: easeOutCubic(span(ms, 0, 1100)),
    crestAlpha: easeOutCubic(span(ms, 120, 260)),
    crestScale: ms < 120 ? 0.35 : lerp(0.35, 1, easeOutBack(span(ms, 120, 460), 2.2)),
    lit: easeInOutCubic(span(ms, ANNOUNCE_IGNITE_AT - 80, ANNOUNCE_IGNITE_AT + 120)),
    flash: ms < ANNOUNCE_IGNITE_AT ? 0 : 1 - easeOutCubic(span(ms, ANNOUNCE_IGNITE_AT, ANNOUNCE_IGNITE_AT + 520)),
    burstT: span(ms, ANNOUNCE_IGNITE_AT, ANNOUNCE_IGNITE_AT + 620),
    ringT: span(ms, ANNOUNCE_IGNITE_AT, ANNOUNCE_IGNITE_AT + 760),
    rule: easeOutCubic(span(ms, 160, 760)),
    overlineAlpha: easeOutCubic(span(ms, 480, 820)),
    overlineRise: 6 * (1 - easeOutCubic(span(ms, 480, 900))),
    titleAlpha: easeOutCubic(span(ms, 600, 980)),
    titleRise: 14 * (1 - easeOutCubic(span(ms, 600, 1150))),
    titleScale: lerp(1.06, 1, easeOutCubic(span(ms, 600, 1150))),
    // 1 = the title in full ember gold, 0 = cooled to its resting cream.
    heat: 1 - easeInCubic(span(ms, 700, 1900)),
    titleShineT: ms > 1150 && ms < 1900 ? easeInOutCubic(span(ms, 1150, 1900)) : -1,
    bodyAlpha: easeOutCubic(span(ms, 850, 1250)),
    bodyRise: 8 * (1 - easeOutCubic(span(ms, 850, 1300))),
    actions: ctaPose(ms - ANNOUNCE_ACTIONS_AT),
    ready: ms >= ANNOUNCE_READY_MS,
})

/**
 * Begin pressed: the crest flares and throws its sparks again, the embers gust upward, and the card
 * pushes in toward the player as it lifts off the world.
 */
export const announceLaunchPose = (ms: number) => ({
    actionsAlpha: 1 - easeOutCubic(span(ms, 0, 200)),
    bodyAlpha: 1 - easeOutCubic(span(ms, 0, 260)),
    flare: ms < 110 ? easeOutCubic(span(ms, 0, 110)) : 1 - easeInOutCubic(span(ms, 110, 700)),
    burstT: span(ms, 0, 600),
    ringT: span(ms, 0, 700),
    gust: easeInCubic(span(ms, 0, 500)),
    push: easeInCubic(span(ms, 120, ANNOUNCE_LAUNCH_MS)),
    contentAlpha: 1 - easeInCubic(span(ms, 220, 680)),
    coverAlpha: 1 - easeInOutCubic(span(ms, 280, ANNOUNCE_LAUNCH_MS)),
    done: ms >= ANNOUNCE_LAUNCH_MS,
})

/** Later: no flourish, the card simply goes. */
export const announceLaterPose = (ms: number) => ({
    contentAlpha: 1 - easeOutCubic(span(ms, 0, 260)),
    coverAlpha: 1 - easeInOutCubic(span(ms, 80, ANNOUNCE_LATER_MS)),
    done: ms >= ANNOUNCE_LATER_MS,
})

/** Fixed-duration transitions between values, so no state change is ever a snap. */
export class Eased {
    private from: number
    private to: number
    private at = -Infinity

    constructor(
        value: number,
        private ms: number,
        private mix: (a: number, b: number, t: number) => number = lerp,
        private ease: (t: number) => number = easeInOutCubic,
    ) {
        this.from = this.to = value
    }

    value = (clock: number) => this.mix(this.from, this.to, this.ease(span(clock - this.at, 0, this.ms)))
    target = () => this.to
    settled = (clock: number) => clock - this.at >= this.ms

    /** Head for `target` from wherever the value is now. */
    set = (target: number, clock: number) => {
        if (target === this.to) return
        this.from = this.value(clock)
        this.to = target
        this.at = clock
    }

    snap = (value: number) => {
        this.from = this.to = value
        this.at = -Infinity
    }
}

/** The current objective's socket breathes, so the eye lands on what to do next. */
export const currentPulse = (ms: number) => 0.5 - 0.5 * Math.cos((ms / 1600) * Math.PI * 2)
